/**
 * Utilitários de CPF/CNPJ do tomador/cliente.
 *
 * O documento é sempre ARMAZENADO só com dígitos (sem máscara). O tipo é
 * detectado pelo tamanho: 11 = CPF, 14 = CNPJ. A máscara é aplicada só na
 * exibição/edição. Também há validação de dígitos verificadores para dar
 * feedback ao usuário — mas o campo é sempre opcional.
 */

export type DocumentKind = 'cpf' | 'cnpj' | null;

/** Remove tudo que não for dígito. */
export function onlyDigits(value: string | null | undefined): string {
  return (value ?? '').replace(/\D/g, '');
}

/** Detecta o tipo do documento pelo número de dígitos. */
export function detectDocumentKind(value: string | null | undefined): DocumentKind {
  const d = onlyDigits(value);
  if (d.length === 11) return 'cpf';
  if (d.length === 14) return 'cnpj';
  return null;
}

/** Aplica a máscara de CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00). */
export function formatDocument(value: string | null | undefined): string {
  const d = onlyDigits(value).slice(0, 14);
  if (d.length <= 11) {
    return d
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  }
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
}

// Máscara de privacidade do documento para exibir em telas/PDF de auditoria sem
// expor o número completo. Mantém os primeiros 3 e os últimos 2 dígitos:
//   CPF  123.456.789-09 → 123.***.**-09
//   CNPJ 12.345.678/0001-99 → 12.***.***/****-99
// Entrada vazia/curta → retorna a formatação normal (nada a esconder).
// (comentário em linha: o exemplo do CNPJ contém "*/", que fecharia um bloco /** */)
export function maskDocument(value: string | null | undefined): string {
  const d = onlyDigits(value);
  if (d.length === 11) {
    return `${d.slice(0, 3)}.***.**-${d.slice(9)}`;
  }
  if (d.length === 14) {
    return `${d.slice(0, 2)}.***.***/****-${d.slice(12)}`;
  }
  return formatDocument(value);
}

/** Máscara de CEP (00000-000). */
export function formatCep(value: string | null | undefined): string {
  const d = onlyDigits(value).slice(0, 8);
  return d.replace(/^(\d{5})(\d)/, '$1-$2');
}

function isValidCpf(digits: string): boolean {
  if (digits.length !== 11 || /^(\d)\1{10}$/.test(digits)) return false;
  const calc = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(digits[i]) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return calc(9) === Number(digits[9]) && calc(10) === Number(digits[10]);
}

function isValidCnpj(digits: string): boolean {
  if (digits.length !== 14 || /^(\d)\1{13}$/.test(digits)) return false;
  const calc = (len: number) => {
    const weights = len === 12
      ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]
      : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    let sum = 0;
    for (let i = 0; i < len; i++) sum += Number(digits[i]) * weights[i];
    const rest = sum % 11;
    return rest < 2 ? 0 : 11 - rest;
  };
  return calc(12) === Number(digits[12]) && calc(13) === Number(digits[13]);
}

/**
 * Valida o documento pelos dígitos verificadores. Retorna:
 *  - true  → CPF ou CNPJ válido
 *  - false → tem 11/14 dígitos mas o DV não confere
 *  - null  → vazio ou comprimento ainda incompleto (não dá para julgar)
 */
export function isValidDocument(value: string | null | undefined): boolean | null {
  const d = onlyDigits(value);
  if (d.length === 0) return null;
  if (d.length === 11) return isValidCpf(d);
  if (d.length === 14) return isValidCnpj(d);
  return null;
}
